import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_Billing360ResponseDados } from '../contracts/Subscriptions_Billing360Response'
import type { Subscriptions_Billing360OverviewRow } from '../contracts/Subscriptions_Billing360OverviewRow'
import type { Subscriptions_Billing360OverviewResponseDados } from '../contracts/Subscriptions_Billing360OverviewResponse'
import type { Subscriptions_EventReprocessResponseDados } from '../contracts/Subscriptions_EventReprocessResponse'
// Side-effect: registers all Billing360 contracts with the responseContract registry.
import '../contracts/Subscriptions_Billing360Response'
import '../contracts/Subscriptions_Billing360SubscriptionRow'
import '../contracts/Subscriptions_Billing360GraceRow'
import '../contracts/Subscriptions_Billing360CreditsRow'
import '../contracts/Subscriptions_Billing360LedgerRow'
import '../contracts/Subscriptions_Billing360OverviewRow'
import '../contracts/Subscriptions_Billing360OverviewResponse'
import '../contracts/Subscriptions_TransactionRow'
import '../contracts/Subscriptions_TransactionAttemptRow'
import '../contracts/Subscriptions_EventRow'
import '../contracts/Subscriptions_EventReprocessInput'
import '../contracts/Subscriptions_EventReprocessResponse'

/**
 * useBillingThreeSixty — SOLE network owner for GET /subscriptions/admin/billing-360/{franquia_id} and overview.
 *
 * Call `fetchBilling360(franchiseId)` to load the composite view for a franchise.
 * Call `fetchOverview()` to load the overview list across all franchises.
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ─────────────────────────────────────────────

const dados = ref<Subscriptions_Billing360ResponseDados | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const overviewRows = ref<Subscriptions_Billing360OverviewRow[]>([])
const overviewLoading = ref(false)
const overviewError = ref<string | null>(null)


// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useBillingThreeSixty() {
  /**
   * fetchBilling360 — GET subscriptions/admin/billing-360/{franquia_id} (staff)
   *               or GET subscriptions/mine/billing-360/{franquia_id} (user).
   */
  async function fetchBilling360(franchiseId: number | string, isStaff: boolean = true): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const endpoint = isStaff
        ? `subscriptions/admin/billing-360/${franchiseId}`
        : `subscriptions/mine/billing-360/${franchiseId}`
      const raw = await httpService.get(
        sistemaUrl(endpoint),
        { responseContract: 'Subscriptions_Billing360Response', skipErrorDisplay: false },
      )
      const resp = raw as { dados?: Subscriptions_Billing360ResponseDados }
      dados.value = resp?.dados ?? null
      log('useBillingThreeSixty', 'fetchBilling360', 'Billing 360 carregado', {
        franchiseId,
        isStaff,
        hasSubscription: !!resp?.dados?.subscription,
      })


    } catch (err) {
      log('useBillingThreeSixty', 'fetchBilling360', 'Erro ao carregar billing 360', {
        franchiseId,
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      error.value = (err as Error)?.message ?? 'Erro desconhecido'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * reprocessEvent — POST subscriptions/admin/events/{id}/reprocess (S9 / M5).
   *
   * Envelope check, NOT dados.success — sendError() replies HTTP 200
   * (MY_Controller::sendResponse()/sendError() never touch the status code),
   * so httpService never throws on a refusal by itself; the envelope's
   * `success` field is the only reliable signal. Same discipline as M1's
   * useSubscriptionAdmin.extend() — a recusa must never read as success.
   */
  async function reprocessEvent(eventId: number): Promise<Subscriptions_EventReprocessResponseDados> {
    const raw = await httpService.post(
      sistemaUrl(`subscriptions/admin/events/${eventId}/reprocess`),
      {},
      {
        inputContract: 'Subscriptions_EventReprocessInput',
        responseContract: 'Subscriptions_EventReprocessResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'reprocess_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_EventReprocessResponseDados }
    log('useBillingThreeSixty', 'reprocessEvent', 'Evento reprocessado', {
      eventId,
      event_type: resp.dados?.event_type,
      new_status: resp.dados?.new_status,
    })
    return resp.dados
  }

  /**
   * fetchOverview — GET subscriptions/admin/billing-360-overview.
   */
  async function fetchOverview(): Promise<Subscriptions_Billing360OverviewRow[]> {
    overviewLoading.value = true
    overviewError.value = null
    try {
      const raw = await httpService.get(
        sistemaUrl('subscriptions/admin/billing-360-overview'),
        { responseContract: 'Subscriptions_Billing360OverviewResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Subscriptions_Billing360OverviewResponseDados }
      overviewRows.value = resp.dados.rows ?? []
      return overviewRows.value
    } catch (err) {
      log('useBillingThreeSixty', 'fetchOverview', 'Erro ao carregar overview billing 360', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      overviewError.value = (err as Error)?.message ?? 'Erro desconhecido'
      throw err
    } finally {
      overviewLoading.value = false
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    dados.value = null
    loading.value = false
    error.value = null
    overviewRows.value = []
    overviewLoading.value = false
    overviewError.value = null
  }

  return {
    // reactive state
    dados,
    loading,
    error,
    overviewRows,
    overviewLoading,
    overviewError,
    // methods
    fetchBilling360,
    fetchOverview,
    reprocessEvent,
    _reset,
  }
}

